import { useState, useEffect, useRef } from "react"
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  UploadOutlined,
  UserOutlined,
  VideoCameraOutlined,
  LogoutOutlined,
  HomeOutlined,
  MedicineBoxOutlined,
  DashboardOutlined,
} from "@ant-design/icons"
import { Button, Layout, Menu, theme, Spin } from "antd"
import { useNavigate, useLocation } from "react-router-dom"
import "../assets/styles/SideBar.css"
import diagnovetAi from "../assets/images/DiagnovetAI.webp"
import icon from "../assets/images/icon.png"
import { getMyUser } from "../services/AuthServices"
import { useAuth } from "../contexts/authContext"
import { doSignOut } from "../firebase/auth"

const { Header, Sider, Content } = Layout

const LoadingScreen = () => (
  <div className="admin-loading-screen">
    <Spin size="large" />
  </div>
)

const SideBar = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false)
  const [isBreakpoint, setIsBreakpoint] = useState(false)
  const [selectedKey, setSelectedKey] = useState("/admin")
  const [loading, setLoading] = useState(true)
  const [isAdmin, setIsAdmin] = useState(false)
  const { getToken } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const verificationDoneRef = useRef(false)

  const {
    token: { colorBgContainer },
  } = theme.useToken()

  useEffect(() => {
    const checkUserRole = async () => {
      if (verificationDoneRef.current && location.pathname.startsWith("/admin")) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setIsAdmin(false)

        const token = await getToken()
        if (!token) {
          throw new Error("No authentication token found.")
        }

        const userData = await getMyUser(token)

        if (!userData.active) {
          navigate("/preconfirmacion")
          return
        } else if (!userData.role || userData.role.name !== "admin") {
          navigate("/dashboard")
          return
        }

        verificationDoneRef.current = true
        setIsAdmin(true)
        setLoading(false)
      } catch (error) {
        console.error("Error al verificar el rol del usuario:", error)
        navigate("/login")
      }
    }

    checkUserRole()
  }, [navigate, getToken, location.pathname])

  useEffect(() => {
    const path = location.pathname
    if (path === "/admin") {
      setSelectedKey("/admin")
    } else if (path.includes("/admin/reportes")) {
      setSelectedKey("/admin/reportes")
    } else if (path.includes("/admin/suscripciones")) {
      setSelectedKey("/admin/suscripciones")
    } else if (path.includes("/admin/usuarios")) {
      setSelectedKey("/admin/usuarios")
    } else if (path.includes("/admin/veterinarias")) {
      setSelectedKey("/admin/veterinarias")
    }
  }, [location])

  const handleMenuClick = ({ key }) => {
    navigate(key)
  }

  const handleLogout = async () => {
    await doSignOut()
    localStorage.clear()
    sessionStorage.clear()
    verificationDoneRef.current = false
    setIsAdmin(false)
    navigate("/login")
  }

  const handleDashboard = () => {
    navigate("/dashboard")
  }

  if (loading || !isAdmin) {
    return <LoadingScreen />
  }

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        collapsedWidth={isBreakpoint ? 0 : 80}
        breakpoint="lg"
        width={200}
        onBreakpoint={(broken) => {
          setIsBreakpoint(broken)
          setCollapsed(broken)
        }}
        className="sider"
      >
        <div className="sider-container">
          <div className="siderupper">
            <img
              src={collapsed && !isBreakpoint ? icon : diagnovetAi}
              alt="logo"
              className={collapsed && !isBreakpoint ? "siderupperlogo-collapsed" : "siderupperlogo"}
            />
            <h3 className={`sideruppertitle ${collapsed ? "collapsed" : ""}`}>Pagina Admin</h3>
          </div>

          <div className="sidercontent">
            <div className="opcionsider">
              <Menu
                mode="inline"
                selectedKeys={[selectedKey]}
                onClick={handleMenuClick}
                items={[
                  {
                    key: "/admin",
                    icon: <HomeOutlined />,
                    label: "Panel Principal",
                  },
                  {
                    key: "/admin/usuarios",
                    icon: <UserOutlined />,
                    label: "Usuarios",
                  },
                  {
                    key: "/admin/reportes",
                    icon: <VideoCameraOutlined />,
                    label: "Reportes",
                  },
                  {
                    key: "/admin/suscripciones",
                    icon: <UploadOutlined />,
                    label: "Suscripciones",
                  },
                  {
                    key: "/admin/veterinarias",
                    icon: <MedicineBoxOutlined />,
                    label: "Veterinarias",
                  },
                ]}
              />
            </div>

            <div className="siderbottom">
              <Button
                type="primary"
                icon={<DashboardOutlined />}
                block
                className="dashboardbutton"
                onClick={handleDashboard}
              >
                {!collapsed && "Ir a Dashboard"}
              </Button>
              <Button type="primary" icon={<LogoutOutlined />} block className="logoutbutton" onClick={handleLogout}>
                {!collapsed && "Cerrar Sesión"}
              </Button>
            </div>
          </div>
        </div>
      </Sider>
      <Layout>
        <Header
          style={{
            padding: 0,
            background: colorBgContainer,
            display: "flex",
            alignItems: "center",
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: "16px",
              width: 64,
              height: 64,
            }}
          />
        </Header>
        <Content
          style={{
            margin: "10px 10px",
            padding: 24,
            background: colorBgContainer,
            borderRadius: "4px",
            minHeight: 280,
          }}
          className="sidebarContent"
        >
          {children}
        </Content>
      </Layout>
    </Layout>
  )
}

export default SideBar
